import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {
    Box,
    Button,
    Chip,
    Container,
    Fade,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    CircularProgress,
    Alert,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import FilterListIcon from '@mui/icons-material/FilterList';
import {motion, AnimatePresence} from 'framer-motion';
import SyllabusForm from './SyllabusForm';
import {api, selectSchoolDetails} from '../../../../common';

// Styled Components
const StyledPaper = styled(Paper)(({theme}) => ({
    borderRadius: 16,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    },
}));

const StyledTableContainer = styled(TableContainer)(({theme}) => ({
    borderRadius: 16,
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.primary.main,
        borderRadius: '10px',
        '&:hover': {
            background: theme.palette.primary.dark,
        },
    },
}));

const StyledFormControl = styled(FormControl)(({theme}) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.3s',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-focused': {
            backgroundColor: theme.palette.background.paper,
        },
    },
}));

const FilterButton = styled(Button)(({theme}) => ({
    borderRadius: 12,
    padding: theme.spacing(1.5, 3),
    textTransform: 'none',
    fontWeight: 600,
}));

const SyllabusList = () => {
    const [syllabi, setSyllabi] = useState([]);
    const [filteredSyllabi, setFilteredSyllabi] = useState([]);
    const [selectedSyllabus, setSelectedSyllabus] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        className: '',
        section: '',
        term: '',
        status: '',
    });
    const theme = useTheme();

    // Get class sections from Redux store
    const classSections = useSelector((state) => state.master?.data?.classSections || []);

    // Get schoolId and session from Redux store
    const userData = useSelector(selectSchoolDetails);
    const schoolId = userData?.id;
    const session = userData?.session;

    const API_URL = `/api/syllabus`;

    useEffect(() => {
        fetchSyllabi();
    }, [schoolId, session]);

    useEffect(() => {
        let filtered = syllabi.filter((syllabus) =>
            Object.values(syllabus)
                .join(' ')
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        );

        // Apply class filter
        if (filters.className) {
            filtered = filtered.filter((syllabus) => syllabus.gradeLevel === filters.className);
        }

        // Apply section filter
        if (filters.section) {
            filtered = filtered.filter((syllabus) => syllabus.section === filters.section);
        }

        // Apply term filter
        if (filters.term) {
            filtered = filtered.filter((syllabus) => syllabus.term === filters.term);
        }

        // Apply status filter
        if (filters.status) {
            filtered = filtered.filter((syllabus) => {
                const status = getStatus(syllabus.startDate, syllabus.endDate);
                return status === filters.status;
            });
        }

        setFilteredSyllabi(filtered);
    }, [syllabi, searchTerm, filters]);

    const fetchSyllabi = async () => {
        if (!schoolId || !session) return;
        try {
            setLoading(true);
            const response = await api.get(API_URL, {params: {schoolId, session}});
            setSyllabi(response.data);
            setFilteredSyllabi(response.data);
        } catch (error) {
            console.error('Error fetching syllabi:', error);
            setError('Failed to fetch syllabi. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (syllabus) => {
        try {
            await api.post(API_URL, syllabus);
            fetchSyllabi();
            setSelectedSyllabus(null);
        } catch (error) {
            console.error('Error saving syllabus:', error);
            setError('Failed to save syllabus. Please try again.');
        }
    };

    const handleEdit = (syllabus) => {
        setSelectedSyllabus(syllabus);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this syllabus?')) {
            try {
                await api.delete(`${API_URL}/${id}`);
                fetchSyllabi();
            } catch (error) {
                console.error('Error deleting syllabus:', error);
                setError('Failed to delete syllabus. Please try again.');
            }
        }
    };

    const handleCancel = () => {
        setSelectedSyllabus(null);
    };

    const handleAdd = () => {
        setSelectedSyllabus({});
    };

    const getStatus = (startDate, endDate) => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (now < start) return 'Upcoming';
        if (now > end) return 'Expired';
        return 'Active';
    };

    const getStatusColor = (startDate, endDate) => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (now < start) return theme.palette.info.main; // Upcoming
        if (now > end) return theme.palette.error.main; // Expired
        return theme.palette.success.main; // Active
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
            ...(field === 'className' && {section: ''}), // Reset section when class changes
        }));
    };

    const clearFilters = () => {
        setFilters({
            className: '',
            section: '',
            term: '',
            status: '',
        });
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{py: 4}}>
                <Fade in timeout={800}>
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            mb: 4,
                        }}
                    >
                        Syllabus Management
                    </Typography>
                </Fade>

                {selectedSyllabus ? (
                    <SyllabusForm
                        currentSyllabus={selectedSyllabus}
                        onSave={handleSave}
                        onCancel={handleCancel}
                    />
                ) : (
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5}}
                    >
                        {/* Search and Add Button */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    placeholder="Search by subject, teacher, grade..."
                                    variant="outlined"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action"/>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        backgroundColor: 'background.paper',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddCircleIcon/>}
                                    onClick={handleAdd}
                                    sx={{
                                        height: '56px',
                                        borderRadius: 3,
                                        boxShadow: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        '&:hover': {
                                            boxShadow: 4,
                                            transform: 'translateY(-2px)',
                                        },
                                        transition: 'all 0.3s',
                                    }}
                                >
                                    Add New Syllabus
                                </Button>
                            </Grid>
                        </Grid>

                        {/* Advanced Filters */}
                        <StyledPaper sx={{mt: 3, p: 3}}>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                <FilterListIcon sx={{mr: 1, color: 'primary.main'}}/>
                                <Typography variant="h6" sx={{fontWeight: 600}}>
                                    Advanced Filters
                                </Typography>
                                <Box sx={{flexGrow: 1}}/>
                                <FilterButton
                                    variant="outlined"
                                    size="small"
                                    onClick={clearFilters}
                                    disabled={!filters.className && !filters.section && !filters.term && !filters.status}
                                >
                                    Clear Filters
                                </FilterButton>
                            </Box>
                            <Grid container spacing={2}>
                                {/* Class Name Filter */}
                                <Grid item xs={12} sm={6} md={3}>
                                    <StyledFormControl fullWidth size="small">
                                        <InputLabel>Class Name</InputLabel>
                                        <Select
                                            value={filters.className}
                                            onChange={(e) => handleFilterChange('className', e.target.value)}
                                            label="Class Name"
                                        >
                                            <MenuItem value="">All Classes</MenuItem>
                                            {classSections?.length > 0 ? (
                                                classSections.map((cls) => (
                                                    <MenuItem key={cls.id} value={cls.name}>
                                                        {cls.name}
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem disabled>No Classes Available</MenuItem>
                                            )}
                                        </Select>
                                    </StyledFormControl>
                                </Grid>

                                {/* Section Filter */}
                                <Grid item xs={12} sm={6} md={3}>
                                    <StyledFormControl fullWidth size="small">
                                        <InputLabel>Section</InputLabel>
                                        <Select
                                            value={filters.section}
                                            onChange={(e) => handleFilterChange('section', e.target.value)}
                                            label="Section"
                                            disabled={!filters.className}
                                        >
                                            <MenuItem value="">All Sections</MenuItem>
                                            {classSections
                                                ?.find((cs) => cs.name === filters.className)
                                                ?.sections?.map((sec) => (
                                                    <MenuItem key={sec.id} value={sec.name}>
                                                        {sec.name}
                                                    </MenuItem>
                                                )) || <MenuItem disabled>No Sections</MenuItem>}
                                        </Select>
                                    </StyledFormControl>
                                </Grid>

                                {/* Term Filter */}
                                <Grid item xs={12} sm={6} md={3}>
                                    <StyledFormControl fullWidth size="small">
                                        <InputLabel>Term</InputLabel>
                                        <Select
                                            value={filters.term}
                                            onChange={(e) => handleFilterChange('term', e.target.value)}
                                            label="Term"
                                        >
                                            <MenuItem value="">All Terms</MenuItem>
                                            <MenuItem value="Term 1">Term 1</MenuItem>
                                            <MenuItem value="Term 2">Term 2</MenuItem>
                                            <MenuItem value="Term 3">Term 3</MenuItem>
                                            <MenuItem value="Semester 1">Semester 1</MenuItem>
                                            <MenuItem value="Semester 2">Semester 2</MenuItem>
                                            <MenuItem value="Annual">Annual</MenuItem>
                                        </Select>
                                    </StyledFormControl>
                                </Grid>

                                {/* Status Filter */}
                                <Grid item xs={12} sm={6} md={3}>
                                    <StyledFormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={filters.status}
                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                            label="Status"
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Upcoming">Upcoming</MenuItem>
                                            <MenuItem value="Expired">Expired</MenuItem>
                                        </Select>
                                    </StyledFormControl>
                                </Grid>
                            </Grid>
                        </StyledPaper>

                        {/* Results Summary */}
                        <Box sx={{mt: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 2}}>
                            <Typography variant="body1" sx={{fontWeight: 500, color: 'text.secondary'}}>
                                Showing {filteredSyllabi.length} of {syllabi.length} syllabi
                            </Typography>
                            {(filters.className || filters.section || filters.term || filters.status) && (
                                <Chip
                                    label="Filtered"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        {/* Table */}
                        <StyledPaper sx={{mt: 2, overflow: 'hidden'}}>
                            {loading ? (
                                <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                                    <CircularProgress/>
                                </Box>
                            ) : error ? (
                                <Box sx={{p: 3}}>
                                    <Alert severity="error">{error}</Alert>
                                </Box>
                            ) : filteredSyllabi.length === 0 ? (
                                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8}}>
                                    <SchoolIcon sx={{fontSize: 64, color: 'text.disabled', mb: 2}}/>
                                    <Typography variant="h6" color="text.secondary">
                                        No syllabi found
                                    </Typography>
                                    <Typography variant="body2" color="text.disabled" sx={{mt: 1}}>
                                        {searchTerm || filters.className || filters.section || filters.term || filters.status
                                            ? 'Try adjusting your filters or search term'
                                            : 'Start by creating a new syllabus'}
                                    </Typography>
                                </Box>
                            ) : (
                                <StyledTableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    '& th': {
                                                        fontWeight: 600,
                                                        fontSize: '0.95rem',
                                                    },
                                                }}
                                            >
                                                <TableCell sx={{color: 'white'}}>Subject</TableCell>
                                                <TableCell sx={{color: 'white'}}>Grade & Section</TableCell>
                                                <TableCell sx={{color: 'white'}}>Term</TableCell>
                                                <TableCell sx={{color: 'white'}}>Teacher</TableCell>
                                                <TableCell sx={{color: 'white'}}>Duration</TableCell>
                                                <TableCell sx={{color: 'white'}}>Status</TableCell>
                                                <TableCell sx={{color: 'white', textAlign: 'center'}}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <AnimatePresence>
                                                {filteredSyllabi.map((syllabus, index) => (
                                                    <TableRow
                                                        component={motion.tr}
                                                        key={syllabus.id}
                                                        initial={{opacity: 0, y: 20}}
                                                        animate={{opacity: 1, y: 0}}
                                                        exit={{opacity: 0, y: -20}}
                                                        transition={{duration: 0.3, delay: index * 0.05}}
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover',
                                                                transform: 'scale(1.01)',
                                                                transition: 'all 0.2s',
                                                            },
                                                            '&:nth-of-type(even)': {
                                                                backgroundColor: 'action.hover',
                                                            },
                                                        }}
                                                    >
                                                        <TableCell>
                                                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                                <SchoolIcon
                                                                    sx={{
                                                                        mr: 1,
                                                                        color: theme.palette.primary.main,
                                                                        fontSize: 28,
                                                                    }}
                                                                />
                                                                <Box>
                                                                    <Typography variant="body1" sx={{fontWeight: 600}}>
                                                                        {syllabus.subjectName}
                                                                    </Typography>
                                                                    {syllabus.description && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {syllabus.description.substring(0, 40)}...
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack spacing={0.5}>
                                                                <Chip
                                                                    label={`Grade ${syllabus.gradeLevel}`}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    sx={{fontWeight: 600}}
                                                                />
                                                                {syllabus.section && (
                                                                    <Chip
                                                                        label={`Section ${syllabus.section}`}
                                                                        size="small"
                                                                        color="secondary"
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={syllabus.term}
                                                                size="small"
                                                                color="info"
                                                                variant="filled"
                                                                sx={{fontWeight: 500}}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                                <PersonIcon sx={{mr: 1, color: 'text.secondary'}}/>
                                                                <Typography variant="body2" sx={{fontWeight: 500}}>
                                                                    {syllabus.teacherName}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                                                                <Box sx={{display: 'flex', alignItems: 'center', mb: 0.5}}>
                                                                    <CalendarTodayIcon sx={{mr: 1, fontSize: 16, color: 'success.main'}}/>
                                                                    <Typography variant="body2" sx={{fontSize: '0.85rem'}}>
                                                                        {syllabus.startDate}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                                    <CalendarTodayIcon sx={{mr: 1, fontSize: 16, color: 'error.main'}}/>
                                                                    <Typography variant="body2" sx={{fontSize: '0.85rem'}}>
                                                                        {syllabus.endDate}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={getStatus(syllabus.startDate, syllabus.endDate)}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: getStatusColor(
                                                                        syllabus.startDate,
                                                                        syllabus.endDate
                                                                    ),
                                                                    color: 'white',
                                                                    fontWeight: 600,
                                                                    minWidth: 80,
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                                <Tooltip title="Edit Syllabus" arrow>
                                                                    <IconButton
                                                                        color="primary"
                                                                        onClick={() => handleEdit(syllabus)}
                                                                        sx={{
                                                                            transition: 'all 0.3s',
                                                                            '&:hover': {
                                                                                backgroundColor: theme.palette.primary.light,
                                                                                transform: 'scale(1.1)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <EditIcon/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Delete Syllabus" arrow>
                                                                    <IconButton
                                                                        color="error"
                                                                        onClick={() => handleDelete(syllabus.id)}
                                                                        sx={{
                                                                            transition: 'all 0.3s',
                                                                            '&:hover': {
                                                                                backgroundColor: theme.palette.error.light,
                                                                                transform: 'scale(1.1)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <DeleteIcon/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </AnimatePresence>
                                        </TableBody>
                                    </Table>
                                </StyledTableContainer>
                            )}
                        </StyledPaper>
                    </motion.div>
                )}
            </Box>
        </Container>
    );
};

export default SyllabusList;
